import ColorThief from 'color-thief-browser';
import Tesseract from 'tesseract.js';

export interface LogoIntelligenceResult {
  primaryColor?: string;
  secondaryColor?: string;
  fontColor?: string;
  companyName?: string;
  palette?: string[];
}

function rgbArrayToHex(rgb: number[]): string {
  const [r, g, b] = rgb.map((value) => Math.max(0, Math.min(255, Math.round(value))));
  const toHex = (value: number) => value.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toLowerCase();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace('#', '');
  if (![3, 6].includes(normalized.length)) {
    return null;
  }

  const value = normalized.length === 3
    ? normalized.split('').map((char) => char + char).join('')
    : normalized;

  const intValue = parseInt(value, 16);
  return {
    r: (intValue >> 16) & 255,
    g: (intValue >> 8) & 255,
    b: intValue & 255,
  };
}

function adjustColorLuminance(hexColor: string, amount: number): string {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;

  const clamp = (value: number) => Math.max(0, Math.min(255, value));

  const adjust = (value: number) => clamp(value + 255 * amount);

  return rgbArrayToHex([adjust(rgb.r), adjust(rgb.g), adjust(rgb.b)]);
}

function getContrastYIQ(hexColor: string): string {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return '#000000';

  const { r, g, b } = rgb;
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? '#111111' : '#ffffff';
}

async function extractPalette(imageSrc: string, colorCount: number = 6): Promise<string[]> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  const palette: string[] = await new Promise((resolve, reject) => {
    image.onload = () => {
      try {
        const colorThief = new ColorThief();
        const dominant = colorThief.getPalette(image, colorCount);
        resolve(dominant.map(rgbArrayToHex));
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = reject;
    image.src = imageSrc;
  });

  return palette;
}

async function extractText(imageSrc: string): Promise<string | undefined> {
  try {
    const { data } = await Tesseract.recognize(imageSrc, 'eng', {
      logger: (m) => { 
        if (m.status === 'recognizing text') { 
        }
      },
    });

    const rawText = data?.text || ''; 

     const words: string[] = [];
    const singleLetters: string[] = [];
       
    const wordsData = (data as any)?.words;
    if (wordsData && Array.isArray(wordsData)) { 
      const wordList = wordsData
        .map((w: any) => w.text?.trim())
        .filter((text: string) => text && /[A-Za-z]/.test(text))
        .map((text: string) => text.replace(/[^A-Za-z0-9 &\-.,]/g, '').trim())
        .filter((text: string) => text.length >= 1);

      for (let i = 0; i < wordList.length; i++) {
        const word = wordList[i];
        if (word.length === 1 && word === word.toUpperCase()) {
          singleLetters.push(word);
        } else if (word.length >= 2) { 
          if (word === word.toUpperCase() && word.length >= 2) {
            words.unshift(word);  
          } else {
            words.push(word);
          }
        }
      }
 
      if (singleLetters.length >= 2 && singleLetters.length <= 10) {
        const acronym = singleLetters.join('');
        if (acronym.length >= 2) {
          words.unshift(acronym); 
        }
      }
    }
 
    const lineCandidates = rawText
      .split(/[\r\n\t\f\v]+/)
      .map((line) => { 
        let cleaned = line.replace(/[^A-Za-z0-9 &\-.,]/g, '').trim(); 
        cleaned = cleaned.replace(/\s{2,}/g, ' ');
        return cleaned;
      })
      .filter((line) => line.length >= 2 && /[A-Za-z]/.test(line));  
    const uppercaseSequence = rawText
      .replace(/[^A-Z\s]/g, '')  
      .replace(/\s+/g, ' ')  
      .trim();
    
    if (uppercaseSequence && uppercaseSequence.length >= 2 && !uppercaseSequence.includes(' ')) { 
      lineCandidates.unshift(uppercaseSequence);
    } else if (uppercaseSequence.includes(' ')) { 
      const acronym = uppercaseSequence.replace(/\s+/g, '');
      if (acronym.length >= 2 && acronym.length <= 10) {
        lineCandidates.unshift(acronym);
      }
    }
 
    const allCandidates = [...words, ...lineCandidates]; 
    if (!allCandidates.length) return undefined;
 
    const sortedCandidates = allCandidates.sort((a, b) => {
      const aIsUpper = a === a.toUpperCase() && a.length >= 2;
      const bIsUpper = b === b.toUpperCase() && b.length >= 2;
      
      if (aIsUpper && !bIsUpper) return -1;
      if (!aIsUpper && bIsUpper) return 1;
       
      return b.length - a.length;
    });
 
    const combined = sortedCandidates[0];
    if (combined && combined.length >= 2) { 
      const shortFragments = sortedCandidates
        .slice(1)
        .filter(c => c.length <= 3 && c === c.toUpperCase())
        .join('');
      
      let result = combined;
      if (shortFragments && combined.length <= 5) { 
        const combinedAttempt = (combined + shortFragments).replace(/[^A-Za-z0-9]/g, '');
        if (combinedAttempt.length >= 2 && combinedAttempt.length <= 20) {
          result = combinedAttempt;
        }
      }

      result = result
        .replace(/\s{2,}/g, ' ')
        .trim()
        .replace(/\s*(LLC|Inc|Corp|Ltd|Co)\.?$/i, '')
        .trim();
 
      return result.length >= 2 ? result : undefined;
    }
 
    const best = sortedCandidates[0];
    if (best && best.length >= 2) {
      let result = best
        .replace(/\s{2,}/g, ' ')
        .trim()
        .replace(/\s*(LLC|Inc|Corp|Ltd|Co)\.?$/i, '')
        .trim();
 
      return result.length >= 2 ? result : undefined;
    }

    return undefined;
  } catch (error) {
    console.error('Logo OCR failed', error);
    return undefined;
  }
}

export async function analyzeLogo(imageSrc?: string): Promise<LogoIntelligenceResult | undefined> {
  if (!imageSrc) return undefined;

  try {
    const [palette, companyName] = await Promise.all([
      extractPalette(imageSrc).catch(() => [] as string[]),
      extractText(imageSrc).catch(() => undefined),
    ]);

    if (!palette.length && !companyName) {
      return { companyName: undefined, palette: [] };
    }

    const [primary, secondaryCandidate] = palette;
    const primaryColor = primary || '#01878f';
    const secondaryColor = secondaryCandidate || adjustColorLuminance(primaryColor, 0.25);
    const fontColor = getContrastYIQ(primaryColor);

    return {
      primaryColor,
      secondaryColor,
      fontColor,
      companyName:"",
      palette,
    };
  } catch (error) {
    console.error('Logo analysis failed', error);
    return undefined;
  }
}

export function mergeLogoInsights(
  current: LogoIntelligenceResult | undefined,
  updates: LogoIntelligenceResult | undefined
): LogoIntelligenceResult | undefined {
  if (!current && !updates) return undefined;
  return {
    primaryColor: updates?.primaryColor ?? current?.primaryColor,
    secondaryColor: updates?.secondaryColor ?? current?.secondaryColor,
    fontColor: updates?.fontColor ?? current?.fontColor,
    companyName: updates?.companyName ?? current?.companyName,
    palette: updates?.palette ?? current?.palette,
  };
}

